import React, { useState, ChangeEvent } from 'react';
import { ApolloQueryResult } from 'apollo-boost';
import { ApolloConsumer } from '@apollo/react-hooks';
import { DropdownWithLabel } from '@blueprint/dropdown';
import { TextField } from '@blueprint/text-input';
import triggerTracking from '@blueprint/tracking';

import {
    PropertiesInPostcodeQuery,
    PropertiesInPostcodeQueryVariables
} from 'valuation/queries/propertiesInPostcode.generated';
import {
    CallToActionRow,
    PropertyBlock,
    TextFieldWrapper,
    ButtonWithIconText
} from './GetHomeWorthEstimateForm.styles';
import sharedTranslations from 'public/locales/en/shared.json';
import { getTranslator } from '../../../shared/i18n/getTranslator';
import { getValidationStatus } from 'shared/helpers/validationDecoration';
import { redirect } from 'shared/helpers/redirect';
import { MAIN_SITE_LINK } from '../../../constants';
import QUERY_PROPERTIES_IN_POSTCODE from 'valuation/queries/propertiesInPostcode.graphql';
import { ButtonWithIcon, Icon } from '@blueprint/ui';

const t = getTranslator(sharedTranslations.get_home_worth_estimate.form);

const postcodeLabel = t('postcode_label');
const addressLabel = t('address_label');
const findAddressButtonText = t('find_address_button');
const getEstimateButtonText = t('get_estimate_button');
const addressDropdownDefaultValue = t('address_dropdown_default_value');
const noPostcodeError = t('postcode_error');
const propertiesInPostcodeQueryError = t('postcode_server_error');

type GetHomeWorthEstimateFormProps = {
    eventCategory: string;
    postcodeId?: string;
};

export const GetHomeWorthEstimateForm = ({
    eventCategory,
    postcodeId = 'postcode'
}: GetHomeWorthEstimateFormProps) => {
    const [properties, setProperties] = useState<
        PropertiesInPostcodeQuery['properties']
    >([]);
    const [error, setError] = useState<string>('');
    const [postcode, setPostcode] = useState<string>('');
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');

    const redirectToPropertyDetailsPage = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'clickthrough',
            eventLabel: 'get_estimate'
        });

        const propertyDetailsPageRoute = `${MAIN_SITE_LINK}/property/${selectedPropertyId}/`;
        redirect(propertyDetailsPageRoute);
    };

    const hasFetchedPropertiesByPostcode = properties && properties.length;
    const hasEnteredPostcode = postcode;
    const hasSelectedAddressFromDropdown = selectedPropertyId;

    const buttonText = hasFetchedPropertiesByPostcode
        ? getEstimateButtonText
        : findAddressButtonText;

    const isButtonDisabled = hasFetchedPropertiesByPostcode
        ? !hasSelectedAddressFromDropdown
        : !hasEnteredPostcode;

    const handleChangePostcode = (event: ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value: newPostcode }
        } = event;

        setProperties([]);
        setSelectedPropertyId('');
        setPostcode(newPostcode);
    };

    const handleChangeAddress = (event: ChangeEvent<HTMLSelectElement>) => {
        const {
            target: { value: propertyId }
        } = event;

        setSelectedPropertyId(propertyId);
    };

    return (
        <ApolloConsumer>
            {(client) => {
                const getPropertiesByPostcode = async () => {
                    let result: ApolloQueryResult<PropertiesInPostcodeQuery>;

                    try {
                        result = await client.query<
                            PropertiesInPostcodeQuery,
                            PropertiesInPostcodeQueryVariables
                        >({
                            query: QUERY_PROPERTIES_IN_POSTCODE,
                            variables: { postcode }
                        });
                    } catch {
                        setProperties([]);
                        setError(propertiesInPostcodeQueryError);
                        return;
                    }

                    triggerTracking({
                        event: 'GAEvent',
                        eventCategory,
                        eventAction: 'click',
                        eventLabel: 'locate_address'
                    });

                    const propertiesFromApi =
                        result.data && result.data.properties;

                    if (propertiesFromApi?.length) {
                        setProperties(propertiesFromApi);
                        setError('');
                    } else {
                        setProperties([]);
                        setError(noPostcodeError);
                    }
                };

                const onSubmit = hasFetchedPropertiesByPostcode
                    ? redirectToPropertyDetailsPage
                    : getPropertiesByPostcode;

                return (
                    <>
                        <TextFieldWrapper>
                            <TextField
                                id={postcodeId}
                                name="postcode"
                                label={postcodeLabel}
                                onChange={handleChangePostcode}
                                validationStatus={getValidationStatus(error)}
                                validationErrorMessage={error}
                                isControlled
                                value={postcode}
                            />
                        </TextFieldWrapper>

                        {hasFetchedPropertiesByPostcode ? (
                            <PropertyBlock>
                                <DropdownWithLabel
                                    hasBorderRadius
                                    id="property-select"
                                    data-testid="property-select"
                                    label={addressLabel}
                                    defaultValue={addressDropdownDefaultValue}
                                    onChange={handleChangeAddress}
                                >
                                    <option
                                        value={addressDropdownDefaultValue}
                                        disabled
                                    >
                                        {addressDropdownDefaultValue}
                                    </option>
                                    {properties.map(
                                        ({ propertyId, fullAddress }) => (
                                            <option
                                                value={propertyId}
                                                key={propertyId}
                                                label={fullAddress || ''}
                                            >
                                                {fullAddress}
                                            </option>
                                        )
                                    )}
                                </DropdownWithLabel>
                            </PropertyBlock>
                        ) : null}
                        <CallToActionRow>
                            <ButtonWithIcon
                                variant="primary"
                                data-testid="estimate-button"
                                onClick={onSubmit}
                                disabled={isButtonDisabled}
                            >
                                <Icon iconId="search"></Icon>
                                <ButtonWithIconText>
                                    {buttonText}
                                </ButtonWithIconText>
                            </ButtonWithIcon>
                        </CallToActionRow>
                    </>
                );
            }}
        </ApolloConsumer>
    );
};
